const API_BASE_URL = import.meta.env.VITE_API_URL || import.meta.env.VITE_API_BASE_URL || 'https://api.midwestrailbirds.com/v1'

export type PushPermissionState = NotificationPermission | 'unsupported'

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = window.atob(base64)
  const outputArray = new Uint8Array(rawData.length)
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i)
  }
  return outputArray
}

export function isPushSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  )
}

export function isIosDevice(): boolean {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent
  const iOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  return iOS
}

export function isStandaloneDisplay(): boolean {
  if (typeof window === 'undefined') return false
  const mediaStandalone = window.matchMedia('(display-mode: standalone)').matches
  const iosStandalone = (window.navigator as Navigator & { standalone?: boolean }).standalone === true
  return mediaStandalone || iosStandalone
}

export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null
  try {
    const registration = await navigator.serviceWorker.register('/sw.js', { scope: '/' })
    await navigator.serviceWorker.ready
    return registration
  } catch (error) {
    console.error('Service worker registration failed', error)
    return null
  }
}

async function authHeaders(getAccessToken: () => Promise<string>): Promise<HeadersInit> {
  const token = await getAccessToken()
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  }
}

export async function fetchVapidPublicKey(getAccessToken: () => Promise<string>): Promise<string> {
  const headers = await authHeaders(getAccessToken)
  const response = await fetch(`${API_BASE_URL}/push/vapid-public-key`, {
    headers,
    credentials: 'include',
  })
  if (!response.ok) {
    throw new Error('Failed to load push configuration')
  }
  const data = await response.json()
  if (!data.publicKey) {
    throw new Error('Push is not configured on the server yet')
  }
  return data.publicKey
}

export async function getSubscriptionCount(getAccessToken: () => Promise<string>): Promise<number> {
  const headers = await authHeaders(getAccessToken)
  const response = await fetch(`${API_BASE_URL}/push/subscriptions`, {
    headers,
    credentials: 'include',
  })
  if (!response.ok) return 0
  const data = await response.json()
  return data.count || 0
}

export interface PushSubscriber {
  userId: string
  name: string
  email: string
  deviceCount: number
  devices: Array<{
    device: string
    createdAt: string | null
    userAgent: string
  }>
}

export async function getPushSubscribers(getAccessToken: () => Promise<string>): Promise<{
  totalUsers: number
  totalDevices: number
  subscribers: PushSubscriber[]
}> {
  const headers = await authHeaders(getAccessToken)
  const response = await fetch(`${API_BASE_URL}/push/subscribers`, {
    headers,
    credentials: 'include',
  })
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.error || 'Failed to load push subscribers')
  }
  return response.json()
}

export async function enablePushNotifications(
  getAccessToken: () => Promise<string>
): Promise<PushSubscription> {
  if (!isPushSupported()) {
    throw new Error('Push notifications are not supported in this browser')
  }

  if (isIosDevice() && !isStandaloneDisplay()) {
    throw new Error('On iPhone, add Midwest Railbirds to your Home Screen, then open it from the icon and enable notifications.')
  }

  const permission = await Notification.requestPermission()
  if (permission !== 'granted') {
    throw new Error('Notification permission was not granted')
  }

  const registration = await registerServiceWorker()
  if (!registration) {
    throw new Error('Could not register service worker')
  }

  const publicKey = await fetchVapidPublicKey(getAccessToken)
  let subscription = await registration.pushManager.getSubscription()

  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    })
  }

  const json = subscription.toJSON()
  const headers = await authHeaders(getAccessToken)
  const response = await fetch(`${API_BASE_URL}/push/subscribe`, {
    method: 'POST',
    headers,
    credentials: 'include',
    body: JSON.stringify({
      endpoint: json.endpoint,
      keys: json.keys,
      userAgent: navigator.userAgent,
    }),
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.error || 'Failed to save push subscription')
  }

  return subscription
}

export async function disablePushNotifications(
  getAccessToken: () => Promise<string>
): Promise<void> {
  const registration = await navigator.serviceWorker.getRegistration()
  const subscription = await registration?.pushManager.getSubscription()
  if (!subscription) return

  const endpoint = subscription.endpoint
  await subscription.unsubscribe()

  const headers = await authHeaders(getAccessToken)
  await fetch(`${API_BASE_URL}/push/subscribe`, {
    method: 'DELETE',
    headers,
    credentials: 'include',
    body: JSON.stringify({ endpoint }),
  })
}

export async function sendTestPush(getAccessToken: () => Promise<string>): Promise<void> {
  const headers = await authHeaders(getAccessToken)
  const response = await fetch(`${API_BASE_URL}/push/test`, {
    method: 'POST',
    headers,
    credentials: 'include',
    body: JSON.stringify({
      title: 'Midwest Railbirds',
      body: 'Push notifications are working on this device!',
      url: '/profile',
    }),
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.error || 'Failed to send test notification')
  }
}

export async function sendAdminPush(
  getAccessToken: () => Promise<string>,
  opts: { userIds: string[]; title?: string; body: string; url?: string }
): Promise<{ recipients: number; sent: number; failed: number; devices: number }> {
  const headers = await authHeaders(getAccessToken)
  const response = await fetch(`${API_BASE_URL}/push/send`, {
    method: 'POST',
    headers,
    credentials: 'include',
    body: JSON.stringify({
      userIds: opts.userIds,
      title: opts.title || 'Midwest Railbirds',
      body: opts.body,
      url: opts.url || '/',
      tag: 'admin-push',
    }),
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.error || 'Failed to send notifications')
  }

  return response.json()
}

export async function getLocalPushState(): Promise<{
  supported: boolean
  permission: PushPermissionState
  subscribed: boolean
}> {
  if (!isPushSupported()) {
    return { supported: false, permission: 'unsupported', subscribed: false }
  }

  const registration = await navigator.serviceWorker.getRegistration()
  const subscription = await registration?.pushManager.getSubscription()
  return {
    supported: true,
    permission: Notification.permission,
    subscribed: !!subscription,
  }
}
